import { computed, unref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useActivityTypeService } from '@/activities/services'
import { useAuthService } from '@/authuser/services'
import icons from '@/base/icons'
import { useRoleHelpers } from '@/group/helpers.js'
import { useCurrentGroupRoles, useSanctionService } from '@/group/services'
import reactiveNow from '@/utils/reactiveNow'

export function useActivityHelpers () {
  const { userId } = useAuthService()
  const roles = useCurrentGroupRoles()
  const { getRoleDisplayName, getRoleSummary } = useRoleHelpers()

  function getIsUserParticipant (activity, participantType = null) {
    if (participantType) {
      return activity.participants.some(participant => participant.user === userId.value && participant.participantType === participantType.id)
    }
    else {
      return activity.participants.some(participant => participant.user === userId.value)
    }
  }

  function getIsEmpty (activity) {
    return activity.participants.length === 0
  }

  function getIsFull (activity, participantType) {
    if (!participantType) throw new Error('must specify participantType')
    if (!participantType.maxParticipants) return false
    const participantCount = activity.participants.filter(participant => participant.participantType === participantType.id).length
    return participantCount >= participantType.maxParticipants
  }

  function getHasStarted (activity) {
    return activity.date <= reactiveNow.value && activity.dateEnd > reactiveNow.value
  }

  function getHasFinished (activity) {
    return activity.dateEnd <= reactiveNow.value
  }

  function getIsUpcoming (activity) {
    return activity.date > reactiveNow.value
  }

  function getIsStartedOrUpcoming (activity) {
    return activity.dateEnd > reactiveNow.value
  }

  const roleOptions = computed(() => roles.value
    .filter(role => !role.isArchived)
    .map(role => ({
      label: getRoleDisplayName(role),
      description: getRoleSummary(role),
      value: role.id,
      role,
    })))

  return {
    getIsUserParticipant,
    getIsEmpty,
    getIsFull,
    getHasStarted,
    getHasFinished,
    getIsUpcoming,
    getIsStartedOrUpcoming,
    roleOptions,
  }
}

export function useActivityTypeHelpers () {
  const { t } = useI18n()

  function getColorName (activityType) {
    if (!activityType) return
    const { id } = activityType
    return `activity-type-${id}`
  }

  function getTextClass (activityType) {
    if (!activityType) return
    return `text-${getColorName(activityType)}`
  }

  function getTranslatedName (activityType) {
    if (!activityType) return
    const { name, nameIsTranslatable } = activityType
    return nameIsTranslatable ? t(`ACTIVITY_TYPE_NAMES.${name}`) : name
  }

  function sortByTranslatedName (a, b) {
    return getTranslatedName(a).localeCompare(getTranslatedName(b))
  }

  function getIconProps (activityType) {
    if (!activityType) return { name: icons.get('activity_fw') }

    const { icon } = activityType
    const title = getTranslatedName(activityType)
    return {
      name: icon,
      color: getColorName(activityType),
      title,

      // can use this in QFabAction if we set "icon" and "label" props...
      // QIcon seems to be OK with these being set too...
      icon,
      label: title,
    }
  }

  function getFeedbackIconProps (activityType) {
    if (!activityType) return
    const { feedbackIcon } = activityType
    return {
      ...getIconProps(activityType),
      name: feedbackIcon,
    }
  }

  return {
    getTextClass,
    getColorName,
    getTranslatedName,
    sortByTranslatedName,
    getIconProps,
    getFeedbackIconProps,
  }
}

export function useActivityTypeTranslatedName (activityType) {
  const { getTranslatedName } = useActivityTypeHelpers()
  return computed(() => getTranslatedName(unref(activityType)))
}

export function useActivityTypes (groupId) {
  const { activityTypes } = useActivityTypeService()
  return computed(() => {
    if (!activityTypes?.value) return []
    if (!groupId) return activityTypes.value
    if (!groupId.value) return []
    const groupIdValue = unref(groupId)
    return activityTypes.value?.filter(placeStatus => placeStatus.group === groupIdValue)
  })
}

// Furthest away expiry sorts first, nulls first (which means infinite expiry)
export function sortSanctionByExpiry (a, b) {
  return (b.expiresAt ?? Number.MAX_SAFE_INTEGER) - (a.expiresAt ?? Number.MAX_SAFE_INTEGER)
}

export function useActivitySanction (placeId) {
  const { activitySanctions } = useSanctionService()
  return computed(() => {
    const sanctions = activitySanctions.value[unref(placeId)]
    if (!sanctions) return null
    return sanctions[0] // they are already sorted, so this is the biggest sanction
  })
}
